import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchCustomerAppointments } from "./calendarApi";

const emptySection = () => ({ rows: [], loading: false, error: "", hasMore: false, nextCursor: null });

const customerPrimary = (customer) => String(customer?.name || customer?.company || customer?.contactPerson || "").trim() || "(no name)";

const customerDetail = (customer) => {
  const company = String(customer?.company || "").trim();
  const contact = String(customer?.contactPerson || "").trim();
  const phone = String(customer?.mobile_phone || customer?.office_phone || customer?.phone || "").trim();
  const email = String(customer?.billingEmail || customer?.email || "").trim();
  return [company, contact, phone, email]
    .filter((value, index, values) => value && value !== customerPrimary(customer) && values.indexOf(value) === index)
    .slice(0, 2)
    .join(" • ");
};

const formatAppointmentDateTime = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(value || ""));
  if (!match) return String(value || "");
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]));
  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const CustomerAppointmentSearch = ({ customers, onOpenAppointment, isMobile }) => {
  const [query, setQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [sections, setSections] = useState({ upcoming: emptySection(), past: emptySection() });
  const requestGeneration = useRef(0);
  const controllers = useRef(new Set());

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    const phoneNeedle = query.replace(/\D/g, "");
    return (customers || []).filter((customer) => {
      const textMatch = [
        customer?.name,
        customer?.company,
        customer?.contactPerson,
        customer?.billingEmail,
        customer?.email,
      ].some((value) => String(value || "").toLowerCase().includes(needle));
      const phoneMatch = phoneNeedle && [
        customer?.mobile_phone,
        customer?.office_phone,
        customer?.phone,
      ].some((value) => String(value || "").replace(/\D/g, "").includes(phoneNeedle));
      return textMatch || phoneMatch;
    }).slice(0, 20);
  }, [customers, query]);

  const cancelRequests = () => {
    requestGeneration.current += 1;
    controllers.current.forEach((controller) => controller.abort());
    controllers.current.clear();
  };

  useEffect(() => () => cancelRequests(), []);

  const loadSection = async (scope, { append = false } = {}) => {
    if (!selectedCustomer) return;
    const generation = requestGeneration.current;
    const cursor = append ? sections[scope].nextCursor : null;
    const controller = new AbortController();
    controllers.current.add(controller);
    setSections((current) => ({
      ...current,
      [scope]: { ...(append ? current[scope] : emptySection()), loading: true, error: "" },
    }));
    try {
      const result = await fetchCustomerAppointments({
        customerId: selectedCustomer.id,
        scope,
        cursor,
        signal: controller.signal,
      });
      if (generation !== requestGeneration.current) return;
      setSections((current) => ({
        ...current,
        [scope]: {
          rows: append ? [...current[scope].rows, ...result.appointments] : result.appointments,
          loading: false,
          error: "",
          hasMore: result.hasMore,
          nextCursor: result.nextCursor,
        },
      }));
    } catch (error) {
      if (error?.name === "AbortError" || generation !== requestGeneration.current) return;
      setSections((current) => ({
        ...current,
        [scope]: { ...current[scope], loading: false, error: error?.message || "Unable to load appointments." },
      }));
    } finally {
      controllers.current.delete(controller);
    }
  };

  useEffect(() => {
    if (!selectedCustomer) return undefined;
    cancelRequests();
    setSections({ upcoming: emptySection(), past: emptySection() });
    const timer = window.setTimeout(() => {
      loadSection("upcoming");
      loadSection("past");
    }, 0);
    return () => window.clearTimeout(timer);
    // The customer selection intentionally starts both first-page requests together.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer]);

  const closeResults = () => {
    cancelRequests();
    setSelectedCustomer(null);
    setSections({ upcoming: emptySection(), past: emptySection() });
  };

  const selectCustomer = (customer) => {
    setQuery(customerPrimary(customer));
    setSelectedCustomer(customer);
  };

  const openAppointment = (appointment) => {
    closeResults();
    setQuery("");
    onOpenAppointment(appointment);
  };

  const renderSection = (scope, heading) => {
    const section = sections[scope];
    return (
      <section style={{ border: "3px solid #000", borderRadius: 12, background: "#365C91", color: "white", padding: 10 }}>
        <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 8 }}>{heading}</div>
        {section.rows.map((appointment) => (
          <button
            key={appointment.id}
            type="button"
            onClick={() => openAppointment(appointment)}
            style={{
              display: "block",
              width: "100%",
              minHeight: 52,
              marginBottom: 8,
              padding: "9px 11px",
              border: "3px solid #E8E2DA",
              borderRadius: 10,
              background: appointment.job_type_color || "white",
              color: "black",
              textAlign: "left",
              cursor: "pointer",
              fontWeight: 800,
            }}
          >
            <div style={{ fontWeight: 900 }}>{formatAppointmentDateTime(appointment.start_time)}</div>
            <div>{String(appointment.title || appointment.job_type_name || "Appointment")}</div>
          </button>
        ))}
        {section.loading ? <div role="status" style={{ fontWeight: 900, padding: 8 }}>LOADING...</div> : null}
        {!section.loading && section.error ? (
          <div role="alert" style={{ border: "3px solid #000", background: "#fee2e2", color: "black", padding: 9, fontWeight: 900 }}>
            <div>{section.error}</div>
            <button type="button" onClick={() => loadSection(scope, { append: section.rows.length > 0 })} style={{ marginTop: 8, minHeight: 42, padding: "6px 14px", border: "3px solid #000", background: "white", fontWeight: 900, cursor: "pointer" }}>RETRY</button>
          </div>
        ) : null}
        {!section.loading && !section.error && section.rows.length === 0 ? (
          <div style={{ fontWeight: 800, padding: 8 }}>No {heading.toLowerCase()} appointments.</div>
        ) : null}
        {!section.loading && !section.error && section.hasMore ? (
          <button type="button" onClick={() => loadSection(scope, { append: true })} style={{ width: "100%", minHeight: 44, border: "3px solid #E8761D", borderRadius: 10, background: "#000", color: "#E8C98F", fontWeight: 900, cursor: "pointer" }}>LOAD MORE</button>
        ) : null}
      </section>
    );
  };

  return (
    <div style={{ position: "relative", flex: 1, minWidth: isMobile ? "100%" : 280 }}>
      <label style={{ display: "block", fontWeight: 900, fontSize: 12, marginBottom: 4 }}>SEARCH CUSTOMER APPOINTMENTS</label>
      <input
        type="search"
        value={query}
        placeholder="Search customer, company, or contact..."
        onChange={(event) => {
          setQuery(event.target.value);
          if (selectedCustomer) closeResults();
        }}
        style={{ width: "100%", minHeight: 46, padding: "9px 12px", border: "3px solid #E8761D", borderRadius: 10, fontWeight: 800, boxSizing: "border-box" }}
      />
      {query.trim() && !selectedCustomer ? (
        <div style={{ position: "absolute", zIndex: 50, top: "100%", left: 0, right: 0, maxHeight: 280, overflowY: "auto", border: "3px solid #000", background: "white", color: "black", boxShadow: "0 8px 18px rgba(0,0,0,0.35)" }}>
          {matches.length ? matches.map((customer) => (
            <button key={customer.id} type="button" onClick={() => selectCustomer(customer)} style={{ display: "block", width: "100%", minHeight: 48, padding: "8px 10px", border: 0, borderBottom: "1px solid #bbb", background: "white", textAlign: "left", cursor: "pointer" }}>
              <div style={{ fontWeight: 900 }}>{customerPrimary(customer)}</div>
              {customerDetail(customer) ? <div style={{ fontSize: 12, fontWeight: 700 }}>{customerDetail(customer)}</div> : null}
            </button>
          )) : <div style={{ padding: 12, fontWeight: 900 }}>No matching customers.</div>}
        </div>
      ) : null}

      {selectedCustomer ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 10020, background: "rgba(0,0,0,0.68)", padding: isMobile ? 10 : 20, boxSizing: "border-box", display: "grid", placeItems: "center" }} onMouseDown={closeResults}>
          <div role="dialog" aria-modal="true" aria-label="Customer appointment history" onMouseDown={(event) => event.stopPropagation()} style={{ width: "100%", maxWidth: 820, maxHeight: "calc(100vh - 32px)", overflowY: "auto", background: "#E8E2DA", border: "6px solid #000", borderRadius: 14, padding: isMobile ? 10 : 16, boxSizing: "border-box" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 900 }}>{customerPrimary(selectedCustomer)}</div>
                {customerDetail(selectedCustomer) ? <div style={{ fontWeight: 800 }}>{customerDetail(selectedCustomer)}</div> : null}
              </div>
              <button type="button" onClick={closeResults} style={{ minWidth: 88, minHeight: 44, border: "3px solid #000", borderRadius: 10, background: "white", fontWeight: 900, cursor: "pointer" }}>CLOSE</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              {renderSection("upcoming", "UPCOMING")}
              {renderSection("past", "PAST")}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CustomerAppointmentSearch;
